import type { Prayer } from '@/types/prayer';

/**
 * Decides whether the inline NameHint affordance should appear under a
 * prayer's `who` row in PrayerDetail. Pure: deterministic on the
 * inputs, no I/O, no React, no DOM — safe to call from any surface.
 *
 * Trigger fires when ALL hold:
 *   1. `language === 'en'`. Spanish/Portuguese marker sets ship as
 *      separate later chunks per the d544f47 sequencing rule for
 *      client-side heuristics — DO NOT extend this function with es
 *      or pt regexes without first locking the marker enumeration
 *      and suppression list per language.
 *   2. The prayer is parsed and classified as `'General'`. A null
 *      `parsed` (parse pending or failed) returns false — the hint
 *      cannot evaluate its own preconditions yet.
 *   3. `userEditedFields.who !== true`. Once the user has manually
 *      committed a `who` for this prayer (rule #37), the hint is
 *      permanently suppressed for that prayer — including after
 *      committing 'General' itself via the "Keep general" chip.
 *   4. `rawText` contains at least one closed pronoun / kinship /
 *      bare-noun / standalone-descriptor marker indicating an
 *      unnamed third-party subject.
 *   5. `rawText` does NOT contain a proper-name suppression hit —
 *      a capitalized non-sentence-start token outside the deity /
 *      reverent-pronoun / weekday / month safe list. Grok's
 *      d544f47 anchor normally classifies named subjects away from
 *      General; this layer is defense-in-depth against rare
 *      misparses that would otherwise produce a wrong-feeling hint
 *      ("name them" when the prayer already names them).
 *
 * The `prayers` corpus parameter is currently unused but kept on the
 * signature for forward-compatibility: future surfaces may want
 * corpus-aware suppression (for example, dampening the hint after
 * the user dismisses similar hints recently, or comparing the raw
 * text against the user's existingPeopleList for soft-match anchors).
 * It is touched via `void prayers` in the body so a refactor sweep
 * does not strip the parameter and break the portable contract.
 */
export function shouldShowNameHint(
  prayer: Prayer,
  prayers: Prayer[],
  language: string,
): boolean {
  // See JSDoc: parameter is reserved for future corpus-aware surfaces.
  // The `void` reference is the lint-shield against a refactor stripping
  // it — DO NOT remove without first confirming no future surface
  // depends on the three-arg signature.
  void prayers;

  if (language !== 'en') return false;
  if (!prayer.parsed || prayer.parsed.who !== 'General') return false;
  if (prayer.userEditedFields?.who === true) return false;

  const raw = prayer.rawText ?? '';
  if (!raw.trim()) return false;

  if (!hasPositiveMarker(raw)) return false;
  if (hasProperNameSuppression(raw)) return false;

  return true;
}

// Positive markers — any one match returns true. Compiled once at module
// load. Validated against 15 sample raws in the Phase 2 plan; 15/15 hit
// the expected outcome. Each class is documented with its rationale so
// a future locale rollout (es / pt) can mirror the structure.
const POSITIVE_MARKER_REGEXES: RegExp[] = [
  // Third-person pronouns. Capitalized reverent forms (He/Him/His) are
  // case-folded by the `i` flag to lowercase here, then handled
  // specifically by the safe-list in proper-name suppression.
  /\b(he|she|him|her|they|them|his|hers|their|theirs)\b/i,
  // Bare-noun "the X" — unnamed third parties referred to by role.
  // Groups (team, church, family-as-org) are intentionally excluded:
  // those are first-class `who` values per the d544f47 anchor and
  // typically don't reach `General` from the parser.
  /\bthe\s+(guy|man|woman|girl|boy|kid|lady|person|family|friend|coworker|neighbor|teacher|student|doctor|nurse|driver|stranger)\b/i,
  // Kinship "my X" — extended/non-immediate family terms only.
  // Mom/Dad/Brother/Sister/Mother/Father/Son/Daughter/Wife/Husband/
  // Grandma/Grandpa are deliberately excluded because the parser's
  // d544f47 anchor classifies them as Title-Case proper subjects
  // (e.g. `who: "Brother"`), not General. If one slips through to
  // General, the user can use chip 2 (Rename) to fix it manually —
  // that's an acceptable miss rate given the parser carries this load.
  /\bmy\s+(friend|coworker|neighbor|teacher|student|doctor|nurse|boss|colleague|roommate|cousin|aunt|uncle|nephew|niece|in[-\s]?law)\b/i,
  // Standalone descriptors — generic third-party references that
  // explicitly signal "unnamed person."
  /\b(someone|somebody|a\s+friend|this\s+person)\b/i,
];

function hasPositiveMarker(rawText: string): boolean {
  return POSITIVE_MARKER_REGEXES.some((re) => re.test(rawText));
}

// Capital-leading tokens that are NOT proper names — deity references,
// reverent capitalized pronouns, weekdays, months. Membership test runs
// against the original-case match; the regex only emits capital-leading
// tokens, so equality against the entries in this set is sufficient.
//
// Reverent He/Him/His are included because written prayer commonly
// capitalizes them when referring to God. False suppression on rare
// human-subject sentence-internal usage ("Mary called and said He's
// coming home" — non-standard capitalization) is preferable to false
// trigger on common deity references in the same surface.
const SAFE_CAPITALIZED_TOKENS = new Set<string>([
  // Religious deity references
  'God',
  'Lord',
  'Jesus',
  'Christ',
  'Father',
  'Heavenly',
  'Holy',
  'Spirit',
  'Almighty',
  'Savior',
  'Saviour',
  'Redeemer',
  'Messiah',
  // Reverent capitalized pronouns
  'You',
  'Your',
  'Yours',
  'Yourself',
  'He',
  'Him',
  'His',
  'Himself',
  'Thee',
  'Thou',
  'Thy',
  'Thine',
  // Weekdays
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
  // Months
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]);

const CAPITAL_TOKEN_RE = /\b[A-Z][a-z]{2,}\b/g;

function hasProperNameSuppression(rawText: string): boolean {
  // Any capital-leading token that is NOT at a sentence-start position
  // AND NOT in the safe list is treated as a proper name. A single hit
  // suppresses the hint.
  for (const match of rawText.matchAll(CAPITAL_TOKEN_RE)) {
    const token = match[0];
    const idx = match.index ?? 0;
    if (isSentenceStart(rawText, idx)) continue;
    if (SAFE_CAPITALIZED_TOKENS.has(token)) continue;
    return true;
  }
  return false;
}

function isSentenceStart(text: string, idx: number): boolean {
  // Walk back from idx-1 over whitespace; the nearest non-whitespace
  // char tells us whether this token sits at a sentence boundary.
  // Handles ". ", "! ", "? ", and a leading newline as sentence
  // terminators. Start-of-string is also sentence-start by definition.
  let i = idx - 1;
  while (i >= 0 && /\s/.test(text[i])) i--;
  if (i < 0) return true;
  const prev = text[i];
  return prev === '.' || prev === '!' || prev === '?' || prev === '\n';
}
